/**
 * Footer V2 - Modern footer with links
 */

import type { TenantInfo } from "@/types/tenant-landing-v2";
import { Heart } from "lucide-react";
import Link from "next/link";

interface FooterV2Props {
  tenant: TenantInfo;
}

export function FooterV2({ tenant }: FooterV2Props) {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="border-t bg-muted/30">
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* About */}
          <div>
            <h3 className="font-bold text-lg mb-3">{tenant.name}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {tenant.description || "Agendamento online simples e eficiente."}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg mb-3">Links Rápidos</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <button
                  onClick={() => {
                    const section = document.getElementById("servicos");
                    section?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  Serviços
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    const section = document.getElementById("avaliacoes");
                    section?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  Avaliações
                </button>
              </li>
              <li>
                <Link
                  href="/termos"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  Termos de Uso
                </Link>
              </li>
              <li>
                <Link
                  href="/privacidade"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  Política de Privacidade
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-bold text-lg mb-3">Contato</h3>
            <ul className="space-y-2 text-sm">
              {tenant.phone && (
                <li>
                  <a
                    href={`tel:${tenant.phone}`}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {tenant.phone}
                  </a>
                </li>
              )}
              {tenant.email && (
                <li>
                  <a
                    href={`mailto:${tenant.email}`}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {tenant.email}
                  </a>
                </li>
              )}
              {tenant.address && (
                <li className="text-muted-foreground">{tenant.address}</li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-6 border-t text-center">
          <p className="text-sm text-muted-foreground flex items-center justify-center gap-1 flex-wrap">
            <span>
              © {currentYear} {tenant.name}. Todos os direitos reservados.
            </span>
            <span className="hidden sm:inline">•</span>
            <span className="flex items-center gap-1">
              Feito com <Heart className="h-3 w-3 text-red-500 fill-red-500" />{" "}
              por{" "}
              <Link
                href="/"
                className="font-semibold hover:text-foreground transition-colors"
              >
                Agendei
              </Link>
            </span>
          </p>
        </div>
      </div>
    </footer>
  );
}
